import { useState } from "react";
import { FeedbackBanner } from "@/core/components/feedback-banner";
import { Button } from "@/core/components/ui/button";
import { Checkbox } from "@/core/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/core/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/core/components/ui/radio-group";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import { formatDateRange } from "@/core/utils/date";
import { cn } from "@/core/utils/utils";
import {
  getCarriedAlongSheetNames,
  getRerunScope,
  namesOwnSheet,
  type RerunUnit,
} from "./rerun-units";
import {
  RerunVersionSelector,
  RerunVersionUnchanged,
} from "./rerun-version-selector";
import {
  AI_SCRIPT_RERUN_VERSION_CHOICE,
  type AiScriptRerunRunOptions,
  type AiScriptRerunSelection,
  type AiScriptRerunVersionChoice,
  type AiScriptRerunVersionInfo,
} from "./types";
import {
  buildAiScriptRerunOptions,
  formatList,
  getAiScriptRerunState,
  getMissingAiScriptRerunVersionReferences,
} from "./utils";

const RERUN_SCOPE = {
  everything: "everything",
  units: "units",
} as const;

type RerunScope = (typeof RERUN_SCOPE)[keyof typeof RERUN_SCOPE];

interface AiScriptRerunConfirmationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (
    runOptions?: AiScriptRerunRunOptions,
    sheetNames?: string[],
  ) => void;
  periodStart: string | undefined;
  periodEnd: string | undefined;
  versionInfo: AiScriptRerunVersionInfo;
  /**
   * The groups of tabs that can be rerun on their own, in workbook order. Empty
   * for a business that hasn't adopted access keys, or whose workbook predates
   * sheet classification — the dialog then offers a whole-workbook rerun only.
   */
  units?: RerunUnit[];
  /** Whether this user may rerun the whole period, not just their own units. */
  canRerunEverything?: boolean;
  /**
   * Whether a run is already in flight for this period. Runs execute one at a
   * time, so this one waits for that one to finish before it starts — worth
   * saying, because the wait is then two full runs rather than one.
   */
  hasRunInProgress?: boolean;
  /**
   * A full-workbook AI run, or an ICM/legacy calculation, is already running.
   * It rewrites everything anyway, so picking single units is pointless while it
   * is in flight.
   */
  isBlockedByOtherRun?: boolean;
}

/**
 * Confirms a rerun and explains what it will do.
 *
 * One dialog for both scopes, reached from the single rerun button, because the
 * choice between "everything" and "just this department" is the thing that needs
 * explaining — and a control per tab could only ever ask half the question.
 *
 * Scope is offered by UNIT rather than by tab: the access key is already the
 * unit of sign-off (approval fingerprints span every sheet sharing a key), so
 * rerunning part of one withdraws the whole unit's approval regardless. Offering
 * the part would be offering a choice with no distinct outcome.
 */
export const AiScriptRerunConfirmationDialog = ({
  open,
  onOpenChange,
  ...contentProps
}: AiScriptRerunConfirmationDialogProps) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <AiScriptRerunConfirmationDialogContent
      key={open ? "open" : "closed"}
      onOpenChange={onOpenChange}
      {...contentProps}
    />
  </Dialog>
);

type AiScriptRerunConfirmationDialogContentProps = Omit<
  AiScriptRerunConfirmationDialogProps,
  "open"
>;

const AiScriptRerunConfirmationDialogContent = ({
  onOpenChange,
  onConfirm,
  periodStart,
  periodEnd,
  versionInfo,
  units = [],
  canRerunEverything = true,
  hasRunInProgress = false,
  isBlockedByOtherRun = false,
}: AiScriptRerunConfirmationDialogContentProps) => {
  const { scriptDrift, rosterDrift, hasDrift, scriptVersion, rosterVersion } =
    getAiScriptRerunState(versionInfo);

  // Units are only worth offering when there is a choice to make: with nothing
  // else runnable, "just this one" and "everything" are the same run.
  const offersUnits = units.length > 0 && !isBlockedByOtherRun;
  const offersScopeChoice = offersUnits && canRerunEverything;
  // Without whole-payroll authority the scope is ALWAYS a set of units, even
  // when there are none to pick: an empty scope means "the whole workbook" to
  // the API, so defaulting to it would be handing a department lead the one run
  // they are not allowed to start. With nothing selected the confirm stays
  // disabled, which is the correct dead end.
  const defaultScope: RerunScope = canRerunEverything
    ? RERUN_SCOPE.everything
    : RERUN_SCOPE.units;
  const [scope, setScope] = useState<RerunScope>(defaultScope);
  // An available unit is selected unless the user removes it. This also selects
  // units that arrive after the dialog opens without restoring units the user
  // already removed.
  const [deselectedUnitIds, setDeselectedUnitIds] = useState<string[]>([]);
  const [scriptChoice, setScriptChoice] =
    useState<AiScriptRerunVersionChoice | null>(
      defaultScope === RERUN_SCOPE.units && scriptDrift
        ? AI_SCRIPT_RERUN_VERSION_CHOICE.keep
        : null,
    );
  const [rosterChoice, setRosterChoice] =
    useState<AiScriptRerunVersionChoice | null>(null);

  // Re-derived from the live list every render, so a unit that started running
  // while the dialog sat open drops out of the scope instead of being submitted
  // for a second recalculation nobody asked for.
  const selectedUnits = units.filter(
    (unit) =>
      !deselectedUnitIds.includes(unit.id) && getRerunScope([unit]).length,
  );
  const scopedSheetNames =
    scope === RERUN_SCOPE.units ? getRerunScope(selectedUnits) : [];
  const isScopedRerun = scope === RERUN_SCOPE.units;
  const rerunSelection: AiScriptRerunSelection = {
    versionInfo,
    scriptChoice,
    rosterChoice,
    isScopedRerun,
  };
  const missingVersionReferences =
    getMissingAiScriptRerunVersionReferences(rerunSelection);
  const hasInvalidVersionReferences = missingVersionReferences.length > 0;

  const handleScopeChange = (next: string) => {
    const nextScope = next as RerunScope;
    setScope(nextScope);
    if (nextScope === RERUN_SCOPE.units && scriptDrift) {
      setScriptChoice(AI_SCRIPT_RERUN_VERSION_CHOICE.keep);
    }
  };

  const handleOpenChange = (nextOpen: boolean) => {
    onOpenChange(nextOpen);
  };

  const handleConfirm = () => {
    handleOpenChange(false);
    onConfirm(buildAiScriptRerunOptions(rerunSelection), scopedSheetNames);
  };

  // Every drifted dimension must be answered before the re-run is enabled.
  const awaitingChoice =
    (scriptDrift && !isScopedRerun && scriptChoice === null) ||
    (rosterDrift && rosterChoice === null);
  const awaitingSelection =
    scope === RERUN_SCOPE.units && scopedSheetNames.length === 0;

  const periodLabel =
    periodStart && periodEnd
      ? ` for ${formatDateRange(periodStart, periodEnd)}`
      : "";

  return (
    <DialogContent
      description="Confirm rerunning payroll for this period"
      className="max-h-[85vh] grid-rows-[auto_minmax(0,1fr)_auto]"
    >
      {/* Capped and row-sized so the BODY scrolls while the title and the
          buttons stay put. Without it a workbook with a few groups, a drift
          choice and the notices grew the dialog past the viewport in both
          directions at once, cutting off its own heading and confirm button.
          Only the header, body and footer are in flow here — the description is
          sr-only and the portal target is absolute — so three rows is right. */}
      <DialogHeader>
        <DialogTitle>Rerun payroll{periodLabel}?</DialogTitle>
      </DialogHeader>
      <Stack className="min-h-0 overflow-y-auto p-6 pt-0" gap="lg" fullWidth>
        {hasDrift && (
          <Typography variant="bodyW1" textColor="secondary">
            The last run for this period used older payroll settings. Choose
            which versions to run with.
          </Typography>
        )}

        {isBlockedByOtherRun && (
          <FeedbackBanner variant="warning">
            <Typography variant="bodyW3">
              A payroll run covering the whole period is already going. It
              recalculates every tab, so there is nothing to pick here.
            </Typography>
          </FeedbackBanner>
        )}

        {offersScopeChoice ? (
          <RadioGroup value={scope} onValueChange={handleScopeChange}>
            <Stack gap="md" fullWidth>
              <ScopeOption
                value={RERUN_SCOPE.everything}
                title="Everything"
                detail="Runs payroll again for the whole period and replaces the Grid."
              />
              <ScopeOption
                value={RERUN_SCOPE.units}
                title="Just some of it"
                detail="Pick the groups to recalculate. Shared dependencies can also recalculate related sheets."
              />
            </Stack>
          </RadioGroup>
        ) : (
          !isBlockedByOtherRun && (
            <Typography variant="bodyW1" textColor="secondary">
              {offersUnits
                ? "Pick what to recalculate. Shared dependencies can also recalculate related sheets."
                : `This will run payroll again${periodLabel} and replace the current Grid.`}
            </Typography>
          )
        )}

        {offersUnits && scope === RERUN_SCOPE.units && (
          <Stack gap="sm" fullWidth>
            {units.map((unit) => (
              <RerunUnitOption
                key={unit.id}
                unit={unit}
                checked={
                  !deselectedUnitIds.includes(unit.id) &&
                  getRerunScope([unit]).length > 0
                }
                onToggle={(checked) =>
                  setDeselectedUnitIds((current) =>
                    checked
                      ? current.filter((id) => id !== unit.id)
                      : current.includes(unit.id)
                        ? current
                        : [...current, unit.id],
                  )
                }
              />
            ))}
          </Stack>
        )}

        {hasInvalidVersionReferences && (
          <FeedbackBanner variant="error">
            <Typography variant="bodyW3">
              Payroll cannot be rerun because this period is missing its saved{" "}
              {missingVersionReferences.join(" and ")} version reference.
              Contact support before rerunning.
            </Typography>
          </FeedbackBanner>
        )}

        {hasDrift && (
          <Stack gap="md" fullWidth>
            {scriptDrift && isScopedRerun ? (
              <Stack gap="xs" fullWidth>
                <RerunVersionUnchanged
                  label="Script"
                  version={versionInfo.runScriptVersion!}
                />
                <Typography variant="bodyW3" textColor="secondary">
                  A section rerun must use the script from the current Grid.
                  Rerun everything to use another script version.
                </Typography>
              </Stack>
            ) : scriptDrift ? (
              <RerunVersionSelector
                label="Script"
                runVersion={versionInfo.runScriptVersion}
                currentVersion={versionInfo.currentScriptVersion}
                choice={scriptChoice}
                onChange={setScriptChoice}
              />
            ) : (
              scriptVersion != null && (
                <RerunVersionUnchanged label="Script" version={scriptVersion} />
              )
            )}
            {rosterDrift ? (
              <RerunVersionSelector
                label="Roster"
                runVersion={versionInfo.runRosterVersion}
                currentVersion={versionInfo.currentRosterVersion}
                choice={rosterChoice}
                onChange={setRosterChoice}
              />
            ) : (
              rosterVersion != null && (
                <RerunVersionUnchanged label="Roster" version={rosterVersion} />
              )
            )}
          </Stack>
        )}

        <FeedbackBanner variant="info">
          <Stack gap="xs" fullWidth>
            <Typography variant="bodyW3">
              <b>This may take a few minutes.</b>{" "}
              {!canRerunEverything
                ? "Full payroll must be recalculated even when only rerunning your section."
                : hasRunInProgress
                  ? "Another payroll run is in progress. This run will begin when it finishes."
                  : "If another payroll run starts first, this run will begin when it finishes."}
            </Typography>
            {scope === RERUN_SCOPE.units && (
              <Typography variant="bodyW3">
                <b>Shared dependencies can recalculate related sheets.</b>
              </Typography>
            )}
            {scope === RERUN_SCOPE.units && (
              <Typography variant="bodyW3">
                <b>Changed approved units need approval again.</b> An approval
                stays in place when its payroll content does not change.
              </Typography>
            )}
            <Typography variant="bodyW3">
              <b>Your manual edits are kept</b> and re-applied. Any edit whose
              underlying numbers changed is flagged for review, and any whose
              row no longer appears is listed as dropped.
            </Typography>
          </Stack>
        </FeedbackBanner>
      </Stack>
      <DialogFooter>
        <Button variant="secondary" onClick={() => handleOpenChange(false)}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={
            awaitingChoice ||
            hasInvalidVersionReferences ||
            awaitingSelection ||
            isBlockedByOtherRun
          }
        >
          {scope === RERUN_SCOPE.everything
            ? "Rerun payroll"
            : // Counts what would actually be submitted, so a unit that went
              // busy while the dialog sat open cannot leave this promising a
              // group it has already dropped.
              selectedUnits.length === 0
              ? "Rerun"
              : selectedUnits.length === 1
                ? `Rerun ${selectedUnits[0].name}`
                : `Rerun ${selectedUnits.length} groups`}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
};

const ScopeOption = ({
  value,
  title,
  detail,
}: {
  value: RerunScope;
  title: string;
  detail: string;
}) => (
  <Stack direction="row" align="start" gap="sm" fullWidth>
    <RadioGroupItem
      value={value}
      id={`rerun-scope-${value}`}
      className="mt-1"
    />
    <label htmlFor={`rerun-scope-${value}`} className="cursor-pointer">
      <Stack gap="none">
        <Typography variant="bodyW2">{title}</Typography>
        <Typography variant="bodyW3" textColor="secondary">
          {detail}
        </Typography>
      </Stack>
    </label>
  </Stack>
);

const RerunUnitOption = ({
  unit,
  checked,
  onToggle,
}: {
  unit: RerunUnit;
  checked: boolean;
  onToggle: (checked: boolean) => void;
}) => {
  const isBusy = getRerunScope([unit]).length === 0;
  const carried = getCarriedAlongSheetNames(unit);
  const id = `rerun-unit-${unit.id}`;
  // Names the tabs it rewrites, then the ones that come along because they total
  // those up — the second half is the part people are surprised by. A lone tab
  // called after its own group says nothing twice, so it says nothing.
  const subline = isBusy
    ? "Already running"
    : [
        namesOwnSheet(unit) ? null : formatList(unit.rerunSheetNames),
        carried.length > 0 &&
          `${formatList(carried)} recalculate${carried.length === 1 ? "s" : ""} with ${unit.rerunSheetNames.length === 1 ? "it" : "them"}`,
      ]
        .filter(Boolean)
        .join(" · ");

  return (
    <Stack
      direction="row"
      align="start"
      gap="sm"
      fullWidth
      className={cn("rounded-md px-1 py-1", { "opacity-50": isBusy })}
    >
      <Checkbox
        id={id}
        checked={checked}
        disabled={isBusy}
        onCheckedChange={(next) => onToggle(next === true)}
        className="mt-0.5"
      />
      <label htmlFor={id} className={cn({ "cursor-pointer": !isBusy })}>
        <Stack gap="none">
          <Typography variant="bodyW3" className="font-semibold">
            {unit.name}
          </Typography>
          {subline && (
            <Typography variant="captionW1" textColor="tertiary">
              {subline}
            </Typography>
          )}
        </Stack>
      </label>
    </Stack>
  );
};
